import Link from 'next/link';
import {
  Accordion,
  AccordionItem,
  AccordionItemButton,
  AccordionItemHeading,
  AccordionItemPanel,
  resetNextUuid
} from 'react-accessible-accordion';

import { useAuth0User } from '@hooks/useAuth0User';
import BetaBadge from '@components/atoms/beta-badge';
import Icon from '@components/atoms/icon';
import { isUserAuthenticated } from '@lib/utils';

const FFHAccordion = ({ closeSidebar, data, setSearchModalIsOpen }) => {
  const { user } = useAuth0User();

  const setCloseSidebar = () => {
    closeSidebar();
  };

  resetNextUuid();

  return (
    <Accordion allowZeroExpanded className="rounded-sm p-4">
      {data.map((navItem) => {
        if (!isUserAuthenticated(user) && navItem.isHiddenForGuests) {
          return;
        }
        // A top-level link with no menu of its own. App and AI Team Rating
        // are drawn below, so only the beta links (Ask FFH) are drawn here.
        if (navItem.isTopLevel && navItem.noChildLinks && navItem.isBeta) {
          return (
            <div key={navItem.level} className="flex items-center p-2 pb-4">
              <Link
                href={navItem.url}
                onClick={() => setCloseSidebar()}
                className="inline-flex items-center font-open-sans text-base font-bold text-white"
              >
                {navItem.name}
                <BetaBadge className="ml-1.5" />
              </Link>
            </div>
          );
        }
        if (navItem.isTopLevel && !navItem.noChildLinks) {
          return (
            <AccordionItem key={navItem.level}>
              <AccordionItemHeading>
                <AccordionItemButton className="relative mb-2 w-full cursor-pointer bg-[#353838] p-2 text-left text-base font-bold text-white after:absolute after:top-3 after:right-0 after:mr-3 after:inline-block after:h-2.5 after:w-2.5 after:-rotate-45 after:border-b-2 after:border-r-2 after:border-current after:content-[''] aria-expanded:after:rotate-45">
                  {navItem.name}
                </AccordionItemButton>
              </AccordionItemHeading>
              <AccordionItemPanel className="mb-3 animate-nav-panel-fade-in bg-[#353838]">
                {data
                  .filter((allItems) => !allItems.isTopLevel)
                  .map((navItemInner) => {
                    if (
                      navItemInner.level.split('.')[0] !==
                      navItem.level.split('.')[0]
                    ) {
                      return null;
                    }
                    if (!navItemInner.url) {
                      return (
                        <p
                          key={navItemInner.level}
                          className="px-4 pt-2 mt-3 rounded-t-lg uppercase font-sans text-xs font-semibold leading-[1.9] tracking-[0.5px] text-white bg-[#2D3030]"
                        >
                          {navItemInner.name}
                        </p>
                      );
                    }
                    return (
                      <Link
                        key={navItemInner.level}
                        href={navItemInner.url}
                        onClick={() => setCloseSidebar()}
                        className="flex items-center bg-[#2D3030] px-[15px] py-3 text-base font-normal text-white no-underline hover:underline last-of-type:rounded-b-lg"
                        target={`${
                          navItemInner.isOpenInNewWindow ? '_blank' : '_self'
                        }`}
                      >
                        {navItemInner.name}
                        {navItemInner.isOpenInNewWindow && (
                          <Icon name="ExternalLink" className="ml-1 w-3 h-3" />
                        )}
                      </Link>
                    );
                  })}
              </AccordionItemPanel>
            </AccordionItem>
          );
        }
      })}
      <div className="flex justify-between items-center p-2 pb-4">
        <a
          className="text-white font-bold font-open-sans text-base inline-block w-full"
          target="_blank"
          rel="noopener noreferrer"
          href="https://onelink.to/ffhubapp"
          onClick={() => setCloseSidebar()}
        >
          App
        </a>
        <Icon className="text-white" name="ExternalLink" />
      </div>
      {!isUserAuthenticated(user) && (
        <div className="flex justify-between items-center p-2 pb-4 mb-3 border-b border-b-white">
          <Link
            href="/ai-team-rating"
            className="text-white font-bold font-open-sans text-base inline-block w-full"
            rel="noopener noreferrer"
            onClick={() => setCloseSidebar()}
          >
            AI Team Rating
          </Link>
        </div>
      )}
      <div
        onClick={() => {
          setSearchModalIsOpen(true);
          setCloseSidebar();
        }}
        className="flex items-center text-white p-2 pb-4 pl-1"
      >
        <Icon className="text-white w-4 h-4" name="Search"></Icon>
        <p className="text-base font-bold text-white ml-2">Search</p>
      </div>
      {isUserAuthenticated(user) && (
        <div className="flex items-center text-white p-2 pl-1 pb-4">
          <Icon className="text-white w-4 h-4" name="Person"></Icon>
          <Link
            href="/account"
            className="text-base font-bold text-white ml-2"
            onClick={() => setCloseSidebar()}
          >
            My Account
          </Link>
        </div>
      )}
    </Accordion>
  );
};

export default FFHAccordion;
